import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Calendar, MapPin, Building2, Filter, ChevronLeft, ChevronRight, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [exhibitions, setExhibitions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ countries: [], industries: [] });
  const [showFilters, setShowFilters] = useState(false);

  const search = searchParams.get("search") || "";
  const country = searchParams.get("country") || "";
  const industry = searchParams.get("industry") || "";
  const page = parseInt(searchParams.get("page") || "0");
  const perPage = 40;

  const fetchExhibitions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * perPage, limit: perPage };
      if (search) params.search = search;
      if (country) params.country = country;
      if (industry) params.industry = industry;
      const res = await axios.get(`${API}/exhibitions`, { params });
      setExhibitions(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, country, industry, page]);

  useEffect(() => { fetchExhibitions(); }, [fetchExhibitions]);
  useEffect(() => { axios.get(`${API}/exhibitions/filters`).then(r => setFilters(r.data)).catch(() => {}); }, []);

  const updateParam = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    p.delete("page");
    setSearchParams(p);
  };

  const totalPages = Math.ceil(total / perPage);
  const activeFilters = [country, industry].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Navbar */}
      <nav className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-12 flex items-center justify-between">
          <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => navigate("/")}>
            <Calendar className="h-5 w-5 text-blue-600" />
            <span className="text-base font-bold tracking-tight">ExpoPulse <span className="text-blue-600">Events</span></span>
          </div>
          <div className="flex items-center gap-5 text-sm">
            <button onClick={() => navigate("/exhibitions")} className="text-gray-900 font-medium text-sm">Exhibitions</button>
            <button onClick={() => navigate("/organizers")} className="text-gray-600 hover:text-gray-900 transition text-sm">Organizers</button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Search bar + filter toggle */}
        <div className="flex gap-2 mb-3">
          <div className="flex-1 flex bg-white border border-gray-300 rounded-md overflow-hidden focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition">
            <div className="flex items-center pl-3"><Search className="h-4 w-4 text-gray-400" /></div>
            <input type="text" placeholder="Search exhibitions..."
              defaultValue={search}
              onKeyDown={(e) => e.key === "Enter" && updateParam("search", e.target.value)}
              className="flex-1 px-3 py-2 text-sm outline-none" />
          </div>
          <button onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-1.5 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm hover:bg-gray-50 transition">
            <Filter className="h-3.5 w-3.5" /> Filters
            {activeFilters > 0 && <span className="bg-blue-600 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{activeFilters}</span>}
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="flex flex-wrap gap-2 mb-3 bg-white border border-gray-200 rounded-md p-3">
            <select value={country} onChange={(e) => updateParam("country", e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-xs bg-white">
              <option value="">All Countries</option>
              {filters.countries?.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={industry} onChange={(e) => updateParam("industry", e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-xs bg-white">
              <option value="">All Industries</option>
              {filters.industries?.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
            {activeFilters > 0 && (
              <button onClick={() => { updateParam("country", ""); updateParam("industry", ""); }}
                className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 px-2">
                <X className="h-3 w-3" /> Clear
              </button>
            )}
          </div>
        )}

        {/* Count */}
        <div className="text-xs text-gray-500 mb-3">
          {total.toLocaleString()} results
          {search && <> for "<span className="font-medium text-gray-700">{search}</span>"</>}
          {country && <> in <span className="font-medium text-gray-700">{country}</span></>}
        </div>

        {/* Grid — 4 columns, compact */}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />
          </div>
        ) : exhibitions.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">No exhibitions found. Try a different search.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {exhibitions.map((ex) => (
              <div key={ex._id} onClick={() => navigate(`/exhibition/${ex._id}`)}
                className="bg-white border border-gray-200 rounded-md p-3 hover:border-blue-300 hover:shadow-sm transition cursor-pointer group">
                <div className="flex items-start gap-2.5">
                  {ex.image_url && (
                    <img src={ex.image_url} alt="" className="w-9 h-9 rounded object-cover bg-gray-100 shrink-0"
                      onError={(e) => e.target.style.display='none'} />
                  )}
                  <div className="min-w-0 flex-1">
                    <h3 className="text-xs font-medium leading-tight group-hover:text-blue-600 transition line-clamp-2">{ex.name}</h3>
                    <div className="flex items-center gap-1 text-[11px] text-gray-400 mt-0.5">
                      <MapPin className="h-2.5 w-2.5 shrink-0" />
                      <span className="truncate">{[ex.city, ex.country].filter(Boolean).join(", ")}</span>
                    </div>
                  </div>
                </div>
                {ex.start_date && (
                  <div className="flex items-center gap-1 text-[11px] text-gray-500 mt-1.5">
                    <Calendar className="h-2.5 w-2.5 shrink-0" />
                    {ex.start_date}
                  </div>
                )}
                {ex.industry && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {ex.industry.split(",").slice(0, 2).map((tag, i) => (
                      <span key={i} className="bg-gray-100 text-gray-500 text-[10px] px-1.5 py-0.5 rounded">{tag.trim()}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-6 mb-4">
            <button disabled={page === 0}
              onClick={() => { const p = new URLSearchParams(searchParams); p.set("page", page - 1); setSearchParams(p); }}
              className="flex items-center gap-1 border border-gray-300 rounded px-3 py-1.5 text-xs disabled:opacity-30 hover:bg-gray-50 transition bg-white">
              <ChevronLeft className="h-3 w-3" /> Prev
            </button>
            <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
            <button disabled={page >= totalPages - 1}
              onClick={() => { const p = new URLSearchParams(searchParams); p.set("page", page + 1); setSearchParams(p); }}
              className="flex items-center gap-1 border border-gray-300 rounded px-3 py-1.5 text-xs disabled:opacity-30 hover:bg-gray-50 transition bg-white">
              Next <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
